<template>
    <UIDialog :open="open" @update:open="$emit('close')">
        <DialogScrollContent
            class="my-4 flex max-h-[90vh] w-full max-w-[95vw] flex-col rounded-lg border-none bg-white p-0 shadow-lg sm:max-w-2xl lg:max-w-4xl"
        >
            <DialogHeader class="border-b border-slate-100">
                <DialogTitle class="px-4 py-4 sm:px-6">
                    <h3 class="text-lg font-semibold text-slate-900">
                        {{ isReply ? 'Reply to Email' : creatorMode ? 'Email Creator' : 'Compose New Email' }}
                    </h3>
                </DialogTitle>
            </DialogHeader>

            <form class="flex min-h-0 flex-1 flex-col" @submit.prevent="sendEmail">
                <div class="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
                    <!-- From -->
                    <div>
                        <label class="mb-1 block text-sm font-medium text-slate-700">From *</label>
                        <select
                            v-model="state.emailAccountId"
                            class="form-select w-full"
                            :disabled="connectedAccounts.length === 0"
                            required
                        >
                            <option value="">Select email account</option>
                            <option v-for="account in connectedAccounts" :key="account.id" :value="account.id">
                                {{ account.email_address }}
                            </option>
                        </select>
                        <div v-if="loadingAccounts" class="mt-1 text-xs text-slate-500">Loading email accounts...</div>
                        <div v-else-if="connectedAccounts.length === 0" class="mt-1 text-xs text-red-600">
                            No connected email accounts available.
                        </div>
                    </div>

                    <!-- List Dropdown (only for new compose) -->
                    <div v-if="!isExistingEmail && !isRecipientLocked">
                        <label class="mb-1 block text-sm font-medium text-slate-700">List *</label>

                        <select
                            v-model="state.selectedListId"
                            class="form-select w-full"
                            :disabled="loadingLists || lists.length === 0"
                        >
                            <option value="">Select a list</option>

                            <optgroup v-for="section in ['My Lists', 'Shared Lists']" :key="section" :label="section">
                                <option
                                    v-for="list in lists.filter((l) => l.section === section)"
                                    :key="list.id"
                                    :value="list.id"
                                >
                                    {{ list.name }} {{ list.totals?.total ? `(${list.totals.total})` : '' }}
                                </option>
                            </optgroup>
                        </select>

                        <div v-if="loadingLists" class="mt-1 text-xs text-slate-500">Loading lists…</div>
                        <div v-else-if="!loadingLists && lists.length === 0" class="mt-1 text-xs text-slate-500">
                            No lists available.
                        </div>

                        <div v-if="state.selectedListId && state.toEmails.length" class="mt-1 text-xs text-slate-600">
                            {{ state.toEmails.length }} recipient{{ state.toEmails.length === 1 ? '' : 's' }} added from
                            the selected list.
                        </div>
                    </div>

                    <!-- To / Recipients -->
                    <div>
                        <label class="mb-1 block text-sm font-medium text-slate-700">Recipients *</label>

                        <!-- Read-only mode for replies, existing, creator, or bulk-selected recipients -->
                        <div v-if="isRecipientLocked">
                            <div
                                v-if="state.toEmails.length"
                                class="mb-2 flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2"
                            >
                                <span
                                    v-for="(recipient, index) in state.toEmails"
                                    :key="index"
                                    class="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300"
                                >
                                    <template v-if="typeof recipient !== 'string' && recipient.creator_name">
                                        {{ recipient.creator_name }} – {{ recipient.email }}
                                    </template>
                                    <template v-else>
                                        {{ typeof recipient === 'string' ? recipient : recipient.email }}
                                    </template>
                                </span>
                            </div>
                            <div
                                v-else
                                class="mb-2 flex min-h-[44px] items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
                            >
                                No recipients with email addresses were added.
                            </div>
                        </div>

                        <!-- Editable mode for new -->
                        <div v-else>
                            <div
                                v-if="state.toEmails.length"
                                class="mb-2 flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2"
                            >
                                <span
                                    v-for="(recipient, index) in state.toEmails"
                                    :key="index"
                                    class="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                                >
                                    {{ recipient.creator_name || recipient.email }} – {{ recipient.email }}
                                    <button
                                        type="button"
                                        class="ml-1 text-indigo-600 hover:text-indigo-800"
                                        @click="removeToEmail(index)"
                                    >
                                        <AppIcon name="times" class="h-3 w-3" />
                                    </button>
                                </span>
                            </div>
                            <div
                                v-else
                                class="mb-2 flex min-h-[44px] items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
                            >
                                {{
                                    state.selectedListId
                                        ? 'No recipients with email addresses were added.'
                                        : 'Select a list to add recipients.'
                                }}
                            </div>

                            <div v-if="loadingCreators" class="mt-1 text-xs text-slate-500">
                                Loading creators from selected list…
                            </div>

                            <div v-else-if="state.toEmails.length" class="mt-1 text-xs text-slate-600">
                                {{ state.toEmails.length }}
                                recipient{{ state.toEmails.length === 1 ? '' : 's' }} added from this list.
                            </div>

                        </div>

                        <div
                            v-if="Array.isArray(missingEmailCreators) && missingEmailCreators.length > 0"
                            class="mt-1 rounded-md bg-amber-50 p-2 text-xs text-amber-700 ring-1 ring-inset ring-amber-600/10"
                        >
                            {{ missingEmailCreators.length }}
                            creator{{ missingEmailCreators.length === 1 ? '' : 's' }} don’t have an email and
                            weren’t added:
                            <span class="font-medium">{{ missingEmailCreators.join(', ') }}</span>
                        </div>

                        <div
                            v-if="Array.isArray(duplicateEmailCreators) && duplicateEmailCreators.length > 0"
                            class="mt-1 rounded-md bg-sky-50 p-2 text-xs text-sky-700 ring-1 ring-inset ring-sky-600/10"
                        >
                            {{ duplicateEmailCreators.length }}
                            duplicate email{{ duplicateEmailCreators.length === 1 ? '' : 's' }} removed:
                            <span class="font-medium">{{ duplicateEmailCreators.join(', ') }}</span>
                        </div>
                    </div>

                    <!-- CC -->
                    <div>
                        <label class="mb-1 block text-sm font-medium text-slate-700">CC</label>
                        <div class="mb-2 flex flex-wrap gap-2">
                            <span
                                v-for="(email, index) in state.ccEmails"
                                :key="index"
                                class="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-600/10"
                            >
                                {{ email }}
                                <button
                                    type="button"
                                    class="ml-1 text-slate-600 hover:text-slate-800"
                                    @click="removeCcEmail(index)"
                                >
                                    <AppIcon name="times" class="h-3 w-3" />
                                </button>
                            </span>
                        </div>
                        <input
                            v-model="state.newCcEmail"
                            type="email"
                            class="form-input w-full"
                            placeholder="Enter CC email address and press Enter"
                            @keydown.enter.prevent="addCcEmail"
                            @blur="addCcEmail"
                        />
                    </div>

                    <!-- Subject -->
                    <div>
                        <label class="mb-1 block text-sm font-medium text-slate-700">Subject *</label>
                        <input
                            ref="subjectInput"
                            v-model="state.subject"
                            type="text"
                            class="form-input w-full"
                            :readonly="isReplyWithPrefilledSubject"
                            :class="{ 'bg-slate-50 text-slate-600': isReplyWithPrefilledSubject }"
                            placeholder="Enter email subject"
                            required
                        />
                        <div v-if="!isExistingEmail" class="mt-1 flex justify-end text-xs text-slate-500">
                            <button
                                type="button"
                                class="rounded bg-slate-100 px-2 py-0.5 hover:bg-slate-200"
                                @click="insertSubjectVariable('{{creator_name}}')"
                            >
                                + Creator Name
                            </button>
                        </div>
                    </div>

                    <!-- Body -->
                    <div>
                        <label class="mb-1 block text-sm font-medium text-slate-700">Message *</label>

                        <EmailRichTextEditor
                            ref="bodyEditor"
                            v-model="state.body"
                            v-model:inline-images="state.inlineImages"
                            :additional-upload-size="attachmentTotalSize"
                            image-mode="attachments"
                            @error="showInlineImageError"
                        />

                        <!-- Merge fields -->
                        <div class="mb-4 mt-1 flex items-center justify-between text-xs text-slate-500">
                            <div>Supports basic formatting.</div>
                            <div class="flex gap-2">
                                <template v-if="!isExistingEmail">
                                    <button
                                        type="button"
                                        class="rounded bg-slate-100 px-2 py-0.5 hover:bg-slate-200"
                                        @click="insertVariable('{{creator_name}}')"
                                    >
                                        + Creator Name
                                    </button>
                                </template>
                            </div>
                        </div>

                        <!-- Signature -->
                        <div class="rounded-md border border-slate-200 bg-slate-50 p-3">
                            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <label class="block text-sm font-medium text-slate-700">Signature</label>
                                    <div v-if="hasSelectedAccountSignature" class="mt-1 text-sm text-slate-500">
                                        {{
                                            state.includeSignature
                                                ? 'Signature will be included when sending.'
                                                : 'Signature will not be included when sending.'
                                        }}
                                    </div>
                                    <div v-else-if="selectedEmailAccount" class="mt-1 text-sm text-slate-500">
                                        No signature for selected sender.
                                    </div>
                                    <div v-else class="mt-1 text-sm text-slate-500">
                                        Select a sender to manage signatures.
                                    </div>
                                </div>

                                <div class="flex flex-wrap items-center gap-2">
                                    <label
                                        v-if="hasSelectedAccountSignature"
                                        class="inline-flex items-center gap-2 text-sm text-slate-600"
                                    >
                                        <input
                                            v-model="state.includeSignature"
                                            type="checkbox"
                                            class="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        Include signature
                                    </label>

                                    <Button
                                        v-if="selectedEmailAccount"
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        @click="openSignatureModal"
                                    >
                                        <AppIcon name="pen-to-square" class="mr-1 h-3 w-3" />
                                        {{ hasSelectedAccountSignature ? 'Edit' : 'Add signature' }}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Attachments -->
                    <FileAttachments
                        :attachments="state.attachments"
                        :additional-size="inlineImagesTotalSize"
                        :additional-original-size="inlineImagesOriginalTotalSize"
                        @update:attachments="state.attachments = $event"
                    />
                </div>

                <!-- Footer -->
                <div
                    class="flex flex-shrink-0 flex-col justify-between gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:px-6"
                >
                    <div class="flex items-center text-sm text-slate-500">
                        <AppIcon
                            v-if="state.isSending"
                            name="spinner-third"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        <template v-if="state.isSending"> Sending email... </template>
                        <template v-else-if="!canSend"> Please fill in all required fields before sending. </template>
                        <template v-else> Ready to send </template>
                    </div>

                    <div class="flex flex-shrink-0 items-center space-x-3">
                        <Button type="button" variant="outline" :disabled="state.isSending" @click="$emit('close')">
                            Cancel
                        </Button>

                        <Button type="submit" :disabled="!canSend || state.isSending">
                            <AppIcon
                                :name="state.isSending ? 'spinner-third' : 'paper-plane'"
                                :class="{ 'animate-spin': state.isSending }"
                                class="mr-1.5 h-3.5 w-3.5"
                                stroke-width="2.5"
                            />
                            {{ isReply ? 'Send Reply' : 'Send Email' }}
                        </Button>
                    </div>
                </div>
            </form>
        </DialogScrollContent>
    </UIDialog>

    <EmailPreviewModal
        :open="previewOpen"
        :emails="previewEmails"
        @close="previewOpen = false"
        @confirm="confirmAndSend"
    />

    <EditSignatureModal
        :open="signatureModalOpen"
        :account="selectedEmailAccount"
        @close="signatureModalOpen = false"
        @updated="onSignatureUpdated"
    />
</template>

<script>
import { Dialog, DialogHeader, DialogTitle, DialogScrollContent } from '@/components/ui/dialog'
import DOMPurify from 'dompurify'
import { Button } from '@/components/ui/button'
import emailAPI from '@/utils/api/api.email'
import { useEmailSender } from '@/composables/email/useEmailSender'
import EmailRichTextEditor from '@/components/email/EmailRichTextEditor.vue'
import FileAttachments from '@/partials/modals/FileAttachments.vue'
import EmailPreviewModal from './EmailPreviewModal.vue'
import EditSignatureModal from './EditSignatureModal.vue'
import { applySignatureToBody, isBlankSignatureHtml, sanitizeEmailSignature } from '@/utils/email/signatures'

export default {
    name: 'EmailComposeModal',
    components: {
        UIDialog: Dialog,
        DialogHeader,
        DialogTitle,
        DialogScrollContent,
        FileAttachments,
        Button,
        EmailRichTextEditor,
        EmailPreviewModal,
        EditSignatureModal,
    },

    props: {
        open: { type: Boolean, default: false },
        prefilledRecipient: { type: String, default: '' },
        prefilledRecipients: { type: Array, default: () => [] },
        prefilledSubject: { type: String, default: '' },
        replyToMessage: { type: Object, default: null },
        replyToConversation: { type: Object, default: null },
        creatorMode: { type: Boolean, default: false },
    },

    emits: ['close', 'sent'],

    data() {
        return {
            state: {
                emailAccountId: '',
                campaignId: '',
                toEmails: [],
                ccEmails: [],
                newCcEmail: '',
                subject: '',
                body: '',
                attachments: [],
                inlineImages: [],
                isSending: false,
                signature: '',
                includeSignature: true,
                selectedListId: '',
            },
            connectedAccounts: [],
            loadingAccounts: true,
            lists: [],
            loadingLists: false,
            loadingCreators: false,
            missingEmailCreators: [],
            duplicateEmailCreators: [],
            previewOpen: false,
            previewEmails: [],
            signatureModalOpen: false,
        }
    },

    computed: {
        isReply() {
            return !!(this.replyToMessage || this.replyToConversation)
        },

        isExistingEmail() {
            return !!(this.replyToMessage || this.replyToConversation)
        },

        hasPrefilledRecipients() {
            return Array.isArray(this.prefilledRecipients) && this.prefilledRecipients.length > 0
        },

        isRecipientLocked() {
            return this.isExistingEmail || this.creatorMode || this.hasPrefilledRecipients
        },

        canSend() {
            const hasAccount = !!this.state.emailAccountId
            const hasRecipients = this.state.toEmails.length > 0
            const hasSubject = !!this.state.subject.trim()
            const editorText = this.$refs.bodyEditor?.getText()?.trim() || ''
            const hasBody = editorText.length > 0 || this.getCurrentInlineImages().length > 0

            return hasAccount && hasRecipients && hasSubject && hasBody && !this.state.isSending
        },

        isReplyWithPrefilledSubject() {
            return !!(this.prefilledSubject || this.replyToMessage || this.replyToConversation)
        },

        selectedEmailAccount() {
            return this.connectedAccounts.find((a) => a.id === this.state.emailAccountId) || null
        },

        selectedAccountSignature() {
            return sanitizeEmailSignature(this.selectedEmailAccount?.signature || '')
        },

        hasSelectedAccountSignature() {
            return !isBlankSignatureHtml(this.selectedAccountSignature)
        },

        attachmentTotalSize() {
            return this.getAttachmentTotalSize()
        },

        inlineImagesTotalSize() {
            return this.getInlineImageTotalSize()
        },

        inlineImagesOriginalTotalSize() {
            return this.getInlineImageOriginalTotalSize()
        },
    },

    watch: {
        open(newVal) {
            if (newVal) {
                this.initializeModal()
                this.$nextTick(() => this.$refs.bodyEditor?.setContent(this.state.body))
                if (!this.isExistingEmail && !this.isRecipientLocked) {
                    this.loadLists()
                }
            } else {
                this.resetForm()
            }
        },
        'state.selectedListId'(newVal) {
            if (newVal) {
                this.loadCreatorsForList(newVal)
            }
        },
        'state.emailAccountId'(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.state.includeSignature = true
            }
        },
    },

    async created() {
        await this.loadConnectedAccounts()
        if (!this.isRecipientLocked) {
            await this.loadLists()
        }
    },

    mounted() {
        if (this.open) {
            this.initializeModal()
            this.$nextTick(() => this.$refs.bodyEditor?.setContent(this.state.body))
        }
    },

    methods: {
        async loadConnectedAccounts() {
            this.loadingAccounts = true
            try {
                const result = await emailAPI.getConnectedAccounts(this.$axios)
                if (result.success) {
                    const allAccounts = result.value.accounts || []
                    this.connectedAccounts = allAccounts.filter((a) => a.sync_status === 'connected')
                    if (this.connectedAccounts.length === 1) {
                        this.state.emailAccountId = this.connectedAccounts[0].id
                    }
                }
            } catch {
                this.connectedAccounts = []
            } finally {
                this.loadingAccounts = false
            }
        },
        openSignatureModal() {
            if (!this.selectedEmailAccount) {
                return
            }

            this.signatureModalOpen = true
        },
        onSignatureUpdated(account) {
            if (!account?.id) {
                return
            }

            const cleanSignature = sanitizeEmailSignature(account.signature || '')
            const index = this.connectedAccounts.findIndex((item) => item.id === account.id)
            const updatedAccount = {
                ...(index >= 0 ? this.connectedAccounts[index] : {}),
                ...account,
                signature: cleanSignature,
            }

            if (index >= 0) {
                this.connectedAccounts.splice(index, 1, updatedAccount)
            } else if (updatedAccount.sync_status === 'connected') {
                this.connectedAccounts.push(updatedAccount)
            }

            if (this.state.emailAccountId === account.id) {
                this.state.includeSignature = !isBlankSignatureHtml(cleanSignature)
            }

            this.signatureModalOpen = false
            this.$successHandler('Signature updated successfully.')
        },
        async loadLists() {
            if (this.isExistingEmail) {
                return
            }
            this.loadingLists = true
            try {
                const { data } = await this.$axios.get('/lists')
                const myLists = Array.isArray(data?.payload?.myLists) ? data.payload.myLists : []
                const sharedLists = Array.isArray(data?.payload?.sharedLists) ? data.payload.sharedLists : []
                const withSection = (arr, section) =>
                    arr.map((l) => ({
                        id: l.id,
                        name: l.name,
                        totals: l.totals || {},
                        section,
                    }))
                this.lists = [...withSection(myLists, 'My Lists'), ...withSection(sharedLists, 'Shared Lists')]
            } catch {
                this.lists = []
            } finally {
                this.loadingLists = false
            }
        },
        async loadCreatorsForList(listId) {
            if (!listId) {
                return
            }
            this.state.toEmails = []
            this.missingEmailCreators = []
            this.duplicateEmailCreators = []
            this.loadingCreators = true
            try {
                const { data } = await this.$axios.get(`/lists/${listId}/creators`)
                const creators = Array.isArray(data?.payload?.creators) ? data.payload.creators : []
                const emails = []
                const missing = []
                const duplicates = []
                const seenEmails = new Set()
                for (const creator of creators) {
                    const email = creator?.superview?.normalized?.email?.trim()
                    const creatorName =
                        creator?.superview?.normalized?.display_name ||
                        creator?.superview?.normalized?.displayName ||
                        creator?.superview?.normalized?.name ||
                        creator?.superview?._id ||
                        'Unknown Creator'
                    if (email && email.includes('@')) {
                        const normalizedEmail = email.toLowerCase()

                        if (seenEmails.has(normalizedEmail)) {
                            duplicates.push(`${creatorName} (${email})`)
                            continue
                        }

                        seenEmails.add(normalizedEmail)

                        emails.push({ email, creator_name: creatorName })
                    } else {
                        missing.push(creatorName)
                    }
                }
                this.state.toEmails = emails
                this.missingEmailCreators = missing
                this.duplicateEmailCreators = duplicates
            } catch (error) {
                console.error('Error loading list creators:', error)
                this.state.toEmails = []
                this.missingEmailCreators = []
                this.duplicateEmailCreators = []
            } finally {
                this.loadingCreators = false
            }
        },

        initializeModal() {
            // Bulk-selected recipients
            if (this.hasPrefilledRecipients) {
                this.setToEmailsFromRecipients(this.prefilledRecipients)
                this.state.subject = this.prefilledSubject || ''
                return
            }

            // Creator mode
            if (this.creatorMode && this.prefilledRecipient && this.prefilledRecipient.includes('@')) {
                this.setToEmailsFromRecipients([{ email: this.prefilledRecipient, creator_name: '' }])
                this.state.subject = this.prefilledSubject || ''
                return
            }

            // Reply to conversation
            if (this.replyToConversation) {
                const recipientEmail = this.replyToConversation.creator_email || ''
                this.setToEmailsFromRecipients([{ email: recipientEmail, creator_name: '' }])
                this.state.subject = this.replyToConversation.subject || ''
                this.state.campaignId = this.replyToConversation.campaign_id || ''
                return
            }

            // Reply to specific message
            if (this.replyToMessage) {
                const recipientEmail = this.replyToMessage.from_email || ''
                this.setToEmailsFromRecipients([{ email: recipientEmail, creator_name: '' }])
                const subject = this.replyToMessage.subject || ''
                this.state.subject = subject.startsWith('Re:') ? subject : `Re: ${subject}`
                return
            }

            // Prefilled compose
            if (this.prefilledRecipient && this.prefilledRecipient.includes('@')) {
                this.setToEmailsFromRecipients([{ email: this.prefilledRecipient, creator_name: '' }])
            }

            if (this.prefilledSubject) {
                this.state.subject = this.prefilledSubject
            }
        },

        resetForm() {
            Object.assign(this.state, {
                emailAccountId: this.connectedAccounts.length === 1 ? this.connectedAccounts[0].id : '',
                campaignId: '',
                toEmails: [],
                ccEmails: [],
                newCcEmail: '',
                subject: '',
                body: '',
                attachments: [],
                inlineImages: [],
                isSending: false,
                signature: '',
                includeSignature: true,
                selectedListId: '',
            })
            this.missingEmailCreators = []
            this.duplicateEmailCreators = []
            this.previewEmails = []
            this.previewOpen = false
            this.signatureModalOpen = false
            this.$refs.bodyEditor?.setContent('')
        },

        addCcEmail() {
            if (this.state.newCcEmail && this.isValidEmail(this.state.newCcEmail)) {
                if (!this.state.ccEmails.includes(this.state.newCcEmail)) {
                    this.state.ccEmails.push(this.state.newCcEmail)
                }
                this.state.newCcEmail = ''
            }
        },

        removeCcEmail(index) {
            this.state.ccEmails.splice(index, 1)
        },

        removeToEmail(index) {
            this.state.toEmails.splice(index, 1)
        },

        isValidEmail(email) {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return regex.test(email)
        },

        getRecipientCreatorName(recipient) {
            if (!recipient || typeof recipient === 'string') {
                return ''
            }

            return (
                recipient.creator_name ||
                recipient.creatorName ||
                recipient.display_name ||
                recipient.displayName ||
                recipient.platform_user_display_name ||
                recipient.platform_user_name ||
                recipient.name ||
                ''
            )
        },

        getRecipientEmail(recipient) {
            return (typeof recipient === 'string' ? recipient : recipient?.email || '').trim()
        },

        getRecipientMissingLabel(recipient) {
            return this.getRecipientCreatorName(recipient) || this.getRecipientEmail(recipient) || 'Unknown Creator'
        },

        setToEmailsFromRecipients(recipients) {
            const emails = []
            const missing = []
            const duplicates = []
            const seenEmails = new Set()

            for (const recipient of recipients || []) {
                const email = this.getRecipientEmail(recipient)
                const creatorName = this.getRecipientCreatorName(recipient)

                if (!email || !this.isValidEmail(email)) {
                    missing.push(this.getRecipientMissingLabel(recipient))
                    continue
                }

                const normalizedEmail = email.toLowerCase()

                if (seenEmails.has(normalizedEmail)) {
                    duplicates.push(creatorName ? `${creatorName} (${email})` : email)
                    continue
                }

                seenEmails.add(normalizedEmail)
                emails.push({ email, creator_name: creatorName })
            }

            this.state.toEmails = emails
            this.missingEmailCreators = missing
            this.duplicateEmailCreators = duplicates
        },

        isAllowedLinkUrl(url = '') {
            return /^(https?:\/\/|mailto:|tel:)/i.test(String(url || '').trim())
        },

        showInlineImageError(message) {
            this.$notify({
                type: 'error',
                title: 'Image upload failed',
                text: message,
                duration: 10000,
                speed: 1000,
            })
        },

        getCurrentEditorBody() {
            return this.$refs.bodyEditor?.getHTML() || this.state.body || ''
        },

        getCurrentInlineImages() {
            return this.$refs.bodyEditor?.getCurrentInlineImages() || []
        },

        getUploadFileSize(file) {
            if (file.content) {
                return file.content.length * 0.75
            }

            return file.size || 0
        },

        getUploadOriginalFileSize(file) {
            return file.original_size || this.getUploadFileSize(file)
        },

        getAttachmentTotalSize() {
            return this.state.attachments.reduce((total, file) => total + this.getUploadFileSize(file), 0)
        },

        getInlineImageTotalSize() {
            return this.state.inlineImages.reduce((total, file) => {
                return total + this.getUploadFileSize(file)
            }, 0)
        },

        getInlineImageOriginalTotalSize() {
            return this.state.inlineImages.reduce((total, file) => {
                return total + this.getUploadOriginalFileSize(file)
            }, 0)
        },

        insertVariable(token) {
            this.$refs.bodyEditor?.insertContent(token)
        },

        insertSubjectVariable(token) {
            const input = this.$refs.subjectInput
            const start = input?.selectionStart ?? this.state.subject.length
            const end = input?.selectionEnd ?? this.state.subject.length

            this.state.subject = `${this.state.subject.slice(0, start)}${token}${this.state.subject.slice(end)}`

            this.$nextTick(() => {
                input?.focus()
                input?.setSelectionRange(start + token.length, start + token.length)
            })
        },

        personalizeSubject(subject, recipient) {
            return this.personalizeMergeFields(subject, recipient, false)
        },

        personalizeBody(body, recipient) {
            return this.personalizeMergeFields(body, recipient, true)
        },

        personalizeMergeFields(value, recipient, includeSignature) {
            let personalized = value || ''
            personalized = personalized.replace(/{{creator_name}}/gi, recipient?.creator_name || '')

            if (includeSignature) {
                personalized = applySignatureToBody(
                    personalized,
                    this.selectedAccountSignature,
                    this.state.includeSignature,
                )
            }

            return personalized
        },

        getInlineImagesForBody(body) {
            return this.$refs.bodyEditor?.getInlineImagesForBody(body) || []
        },

        getAttachmentsForBody(body) {
            return [...this.state.attachments, ...this.getInlineImagesForBody(body)]
        },

        replaceCidImagesForPreview(body) {
            let previewBody = body

            this.getInlineImagesForBody(body).forEach((image) => {
                const dataUrl = `data:${image.content_type};base64,${image.content}`
                const pattern = new RegExp(`cid:${this.escapeRegExp(image.content_id)}`, 'g')
                previewBody = previewBody.replace(pattern, dataUrl)
            })

            return previewBody
        },

        normalizeLinksInDocument(document) {
            document.querySelectorAll('a[href]').forEach((link) => {
                const href = (link.getAttribute('href') || '').trim()

                if (!this.isAllowedLinkUrl(href)) {
                    this.unwrapElement(link)
                    return
                }

                link.setAttribute('href', href)
                link.setAttribute('target', '_blank')
                link.setAttribute('rel', 'noopener noreferrer')
            })
        },

        normalizeBodyLinks(body) {
            if (typeof DOMParser === 'undefined') {
                return body || ''
            }

            const parser = new DOMParser()
            const document = parser.parseFromString(body || '', 'text/html')
            this.normalizeLinksInDocument(document)

            return document.body.innerHTML
        },

        unwrapElement(element) {
            const parent = element.parentNode

            if (!parent) {
                return
            }

            while (element.firstChild) {
                parent.insertBefore(element.firstChild, element)
            }

            parent.removeChild(element)
        },

        prepareSendBody(body) {
            const parser = new DOMParser()
            const document = parser.parseFromString(body || '', 'text/html')
            this.normalizeLinksInDocument(document)

            document.querySelectorAll('img[data-content-id]').forEach((image) => {
                const contentId = image.getAttribute('data-content-id')

                if (contentId) {
                    image.setAttribute('src', `cid:${contentId}`)
                }
            })

            return document.body.innerHTML
        },

        preparePreviewBody(body) {
            const previewBody = this.normalizeBodyLinks(this.replaceCidImagesForPreview(body))

            return DOMPurify.sanitize(previewBody, {
                ALLOWED_TAGS: [
                    'p',
                    'br',
                    'div',
                    'span',
                    'strong',
                    'em',
                    'u',
                    'i',
                    'b',
                    'a',
                    'img',
                    'ul',
                    'ol',
                    'li',
                    'blockquote',
                    'table',
                    'thead',
                    'tbody',
                    'tfoot',
                    'tr',
                    'td',
                    'th',
                    'center',
                    'font',
                    'small',
                    'big',
                    'sub',
                    'sup',
                    'hr',
                ],
                ALLOWED_ATTR: [
                    'href',
                    'src',
                    'alt',
                    'title',
                    'target',
                    'rel',
                    'style',
                    'class',
                    'data-align',
                    'data-size',
                    'data-content-id',
                    'width',
                    'height',
                    'border',
                    'cellpadding',
                    'cellspacing',
                    'align',
                    'color',
                    'bgcolor',
                    'size',
                    'face',
                    'valign',
                ],
                ALLOW_DATA_ATTR: false,
                ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpeg|jpg|gif|webp);base64,)/i,
                FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe'],
                FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur', 'onsubmit'],
            })
        },

        escapeRegExp(str = '') {
            return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        },

        async sendEmail() {
            if (!this.canSend) {
                return
            }

            // Build preview email list
            const editorBodyTemplate = this.getCurrentEditorBody()

            this.previewEmails = this.state.toEmails.map((toEmail) => ({
                to: toEmail.email,
                subject: this.personalizeSubject(this.state.subject, toEmail),
                body: this.preparePreviewBody(this.personalizeBody(editorBodyTemplate, toEmail)),
            }))

            // Open preview modal
            this.previewOpen = true
        },

        async confirmAndSend() {
            this.previewOpen = false
            this.state.isSending = true

            const { sendEmail } = useEmailSender(this.$axios, {
                onSuccess: async (value) => {
                    this.state.isSending = false
                    if (!value?.job_id) {
                        this.$successHandler('Email sent')
                        this.$emit('sent', value)
                        this.$emit('close')
                        return
                    }

                    const totalCount = value.total_count || personalizedRecipients.length
                    const backgroundActionId = await this.$store.dispatch('addBackgroundAction', {
                        description: `Sending ${totalCount} email${totalCount === 1 ? '' : 's'}`,
                        progressMessage: 'Queued...',
                    })

                    this.$store.dispatch('startPolling', {
                        id: backgroundActionId,
                        pollUrl: `/jobs/${value.job_id}`,
                        onComplete: (statusData) => {
                            this.$store.dispatch('updateBackgroundAction', {
                                id: backgroundActionId,
                                updates: { statusMessage: statusData.status_message },
                            })
                            this.$store.dispatch('completeBackgroundAction', { id: backgroundActionId })
                        },
                        onFail: (error) => {
                            this.$store.dispatch('failBackgroundAction', {
                                id: backgroundActionId,
                                error,
                            })
                        },
                    })

                    this.$emit('sent', value)
                    this.$emit('close')
                },
                onError: (message) => {
                    this.state.isSending = false
                    this.$errorHandler(message)
                },
            })

            const bodyTemplate = this.getCurrentEditorBody()
            const personalizedRecipients = this.state.toEmails.map((toEmail) => ({
                ...toEmail,
                subject: this.personalizeSubject(this.state.subject, toEmail),
                body: this.prepareSendBody(this.personalizeBody(bodyTemplate, toEmail)),
            }))
            const attachments = this.getAttachmentsForBody(bodyTemplate)

            await sendEmail({
                state: {
                    ...this.state,
                    body: this.prepareSendBody(bodyTemplate),
                    toEmails: personalizedRecipients,
                    attachments,
                },
                replyToMessage: this.replyToMessage,
                replyToConversation: this.replyToConversation,
            })
        },
    },
}
</script>
